import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { assistantApi, type AskActive, type AskConversation, type AskEvent } from '../../api/assistantApi';
import { deriveAskContext, toWire, type AskContext } from '../../lib/askContext';
import { foldEvents, statusLine, type TimelineItem } from '../../lib/askTimeline';
import { useStore } from '../../store/StoreContext';

// One conversation for the whole app. The server owns it (history, steps,
// approvals); the browser only remembers its id and which channel is pinned,
// so a reload, a tab switch or a restart of the page loses nothing.
//
// History belongs to a channel: the conversation shown is the one about the
// channel the page (or the dropdown pin) points at. Pages with no channel,
// such as My Channels, are a fresh chat.

interface AskApi {
  context: AskContext;
  pinned: string;
  setPinned: (nick: string) => void;
  items: TimelineItem[];
  conversation: AskConversation | null;
  conversations: AskConversation[];
  switchConversation: (id: string) => void;
  newConversation: () => void;
  status: { text: string; tone: 'idle' | 'working' | 'ready' | 'attention' | 'error' };
  active: AskActive | null;
  busy: boolean;
  error: string;
  send: (text: string) => Promise<boolean>;
  decide: (approvalId: string, decision: 'approve' | 'deny') => Promise<void>;
  cancel: (execId: string) => Promise<void>;
  /** A finished build waiting for the user because they navigated away. */
  readyToOpen: { url: string; label: string } | null;
  dismissReady: () => void;
  go: (url: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  full: boolean;
  setFull: (v: boolean) => void;
}

const Ctx = createContext<AskApi | null>(null);

export function useAsk(): AskApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAsk outside AskProvider');
  return v;
}

const uuid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now());

export function AskProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, channels, isChannelOwner, syncChannels, aiSettings } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [convId, setConvId] = useState<string>('');
  const [conversation, setConversation] = useState<AskConversation | null>(null);
  const [conversations, setConversations] = useState<AskConversation[]>([]);
  const [events, setEvents] = useState<AskEvent[]>([]);
  const [active, setActive] = useState<AskActive | null>(null);
  const [pinned, setPinnedState] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [readyToOpen, setReadyToOpen] = useState<{ url: string; label: string } | null>(null);

  const afterSeq = useRef(0);
  const convRef = useRef(convId);
  convRef.current = convId;
  // Where the user was when they sent the message that started a run. If they
  // are still there when it finishes we open the result; if not, we never
  // steal their screen (the dock offers "Open Report" instead).
  const watch = useRef<{ path: string; fromSeq: number } | null>(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // The context follows navigation immediately; it is read from the URL and
  // the store's own channel record.
  const context = useMemo(() => {
    const m = location.pathname.match(/^\/(?:channels|public)\/([^/]+)/);
    const nick = m ? decodeURIComponent(m[1]) : '';
    const c = nick ? channels.find((x) => x.nickname === nick || x.id === nick) : undefined;
    return deriveAskContext(
      location.pathname,
      c ? { title: c.title, isPublic: c.isPublic, owner: isChannelOwner(c), viewType: c.viewType } : undefined,
    );
  }, [location.pathname, channels, isChannelOwner]);

  const setPinned = useCallback((nick: string) => {
    setPinnedState(nick);
  }, []);

  const reset = useCallback((id: string) => {
    afterSeq.current = 0;
    setEvents([]);
    setActive(null);
    setConversation(null);
    setConvId(id);
    convRef.current = id;
  }, []);

  const apply = useCallback(
    async (evs: AskEvent[]) => {
      if (evs.length === 0) return;
      afterSeq.current = Math.max(afterSeq.current, evs[evs.length - 1].seq);
      setEvents((prev) => {
        const seen = new Set(prev.map((e) => e.seq));
        const fresh = evs.filter((e) => !seen.has(e.seq));
        return fresh.length ? [...prev, ...fresh].sort((a, b) => a.seq - b.seq) : prev;
      });
      // A build that finished while the user is still where they asked for it.
      for (const e of evs) {
        const w = watch.current;
        if (!w || e.kind !== 'result' || e.seq <= w.fromSeq) continue;
        const open = e.actions?.find((a) => a.kind === 'open');
        const kind = e.data?.kind;
        if (!open || (e.status !== 'completed' && e.status !== 'partial') || (kind !== 'build' && kind !== 'add_company')) continue;
        watch.current = null;
        if (pathRef.current === w.path) {
          await syncChannels();
          navigate(open.url);
        } else {
          setReadyToOpen({ url: open.url, label: open.label || 'Open Report' });
        }
      }
    },
    [navigate, syncChannels],
  );

  const poll = useCallback(async (): Promise<boolean> => {
    const id = convRef.current;
    if (!id) return false;
    const res = await assistantApi.events(id, afterSeq.current);
    if (convRef.current !== id) return false;
    if (!res.ok) {
      if (res.status === 404) reset(''); // conversation expired or is not ours
      else setError(res.error);
      return false;
    }
    setError('');
    setConversation(res.data.conversation);
    setActive(res.data.active ?? null);
    await apply(res.data.events);
    return !!res.data.active;
  }, [apply, reset]);

  // Initial load + adaptive polling: fast while something runs, slow when
  // idle, slower still when the panel is closed, paused while the tab is hidden.
  useEffect(() => {
    if (!isLoggedIn) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (stop) return;
      let running = false;
      if (document.visibilityState !== 'hidden') running = await poll();
      if (stop) return;
      const wait = running ? 1000 : open ? 4000 : 15000;
      timer = setTimeout(tick, wait);
    };
    void tick();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        if (timer) clearTimeout(timer);
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isLoggedIn, convId, open, poll]);

  // The route wins over an old pin: opening another channel drops the pin.
  const routeChannel = context.channel ?? '';
  const lastRoute = useRef(routeChannel);
  useEffect(() => {
    if (lastRoute.current === routeChannel) return;
    lastRoute.current = routeChannel;
    if (routeChannel && pinned && pinned !== routeChannel) setPinned('');
  }, [routeChannel, pinned, setPinned]);

  // Show the history of whatever channel we are looking at; no channel = new chat.
  const scope = pinned || routeChannel;
  useEffect(() => {
    if (!isLoggedIn) return;
    let stale = false;
    void (async () => {
      let target = '';
      if (scope) {
        const r = await assistantApi.list();
        if (stale) return;
        if (r.ok) {
          setConversations(r.data.conversations);
          const cur = r.data.conversations.find((c) => c.id === convRef.current);
          // already showing this channel's chat (e.g. just created by a send)
          if (cur && cur.activeChannel === scope) return;
          target = r.data.conversations.find((c) => c.activeChannel === scope)?.id ?? '';
        }
      }
      if (target !== convRef.current) reset(target);
    })();
    return () => {
      stale = true;
    };
  }, [scope, isLoggedIn, reset]);

  // Conversation list for the switcher.
  useEffect(() => {
    if (!isLoggedIn || !open) return;
    void assistantApi.list().then((r) => r.ok && setConversations(r.data.conversations));
  }, [isLoggedIn, open, conversation?.updatedAt]);

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const t = text.trim();
      if (!t || busy) return false;
      setBusy(true);
      setError('');
      const fromSeq = afterSeq.current;
      const key = aiSettings?.apiKeys?.[aiSettings.provider]?.trim();
      const res = await assistantApi.send(convRef.current || null, t, toWire(context, pinned), uuid(), aiSettings?.provider ? { provider: aiSettings.provider, ...(key ? { apiKey: key } : {}) } : undefined);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      const r = res.data.result;
      if (r.conversationId !== convRef.current) {
        convRef.current = r.conversationId;
        afterSeq.current = 0;
        setConvId(r.conversationId);
      }
      watch.current = { path: pathRef.current, fromSeq: Math.max(fromSeq, 0) };
      setReadyToOpen(null);
      setOpen(true);
      await poll();
      return true;
    },
    [busy, context, pinned, poll, aiSettings],
  );

  const decide = useCallback(
    async (approvalId: string, decision: 'approve' | 'deny') => {
      setBusy(true);
      const res = await assistantApi.decide(approvalId, decision);
      setBusy(false);
      if (!res.ok) setError(res.error);
      await poll();
    },
    [poll],
  );

  const cancel = useCallback(
    async (execId: string) => {
      const res = await assistantApi.cancel(execId);
      if (!res.ok) setError(res.error);
      await poll();
    },
    [poll],
  );

  const items = useMemo(() => foldEvents(events), [events]);
  const status = useMemo(() => statusLine(items, active?.summary || (active ? 'Working…' : undefined)), [items, active]);

  const value: AskApi = {
    context,
    pinned,
    setPinned,
    items,
    conversation,
    conversations,
    switchConversation: (id) => reset(id),
    newConversation: () => reset(''),
    status,
    active,
    busy,
    error,
    send,
    decide,
    cancel,
    readyToOpen,
    dismissReady: () => setReadyToOpen(null),
    go: (url) => navigate(url),
    open,
    setOpen,
    full,
    setFull,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
